import { Box, CheckboxOption, Input, Link, Select } from '@nerdwallet/currency';
import ImageIcon from '@nerdwallet/react-icon/material/Image';
import Image, { ImageProps } from '~/app/components/image/image';
import { Fragment, useMemo, useState } from 'react';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import 'react-tabs/style/react-tabs.scss';
import SegmentImpression from '~/app/components/segment-impression';
import { useDynamicGrid } from '~/app/lib/redux/selectors/dynamic-grid-selector';
import { useSearchParams } from 'next/navigation';
import {
  SegmentTrackingForm,
  SegmentTrackingProps,
} from '../../segment/segment-tracking-form';
import { useDynamicAnalytics } from '../../segment/use-dynamic-analytics';
import { DynamicImageWpMedia } from './dynamic-image-wp-media';
import {
  IMAGE_SOURCE_TYPES,
  useDynamicImageForm,
} from './use-dynamic-image-form';

import styles from './dynamic-image.module.scss';
import { buttonUrlWithCustomQueryParams } from '../../dynamic-grid-helpers';

const imageObjectFitOptions = [
  {
    label: 'Cover',
    value: 'cover',
  },
  {
    label: 'Contain',
    value: 'contain',
  },
  {
    label: 'Fill',
    value: 'fill',
  },
  {
    label: 'None',
    value: 'none',
  },
  {
    label: 'Scale Down',
    value: 'scale-down',
  },
];

const imageObjectPositionOptions = [
  {
    label: 'None',
    value: undefined,
  },
  {
    label: 'Center',
    value: 'center',
  },
  {
    label: 'Top',
    value: 'top',
  },
  {
    label: 'Bottom',
    value: 'bottom',
  },
  {
    label: 'Left',
    value: 'left',
  },
  {
    label: 'Right',
    value: 'right',
  },
];

const ImageWrapper = ({
  imageLinkUrl,
  children,
}: {
  imageLinkUrl?: string;
  children: React.ReactNode;
}) => {
  if (imageLinkUrl) {
    return (
      // Use encodeURI to escape imageLinkUrl before passing it to href prop
      <Link
        style={{ width: 'inherit', height: 'inherit' }}
        href={encodeURI(imageLinkUrl || '')}
      >
        {children}
      </Link>
    );
  }
  return children;
};

interface DynamicImageComponentProps {
  imageUrl?: string;
  imageLinkUrl?: string;
  rolloverImageUrl?: string;
  width?: number | 'auto';
  height?: number;
  objectFit?: string;
  circularImage?: boolean;
  altText?: string;
  objectPosition?: ImageProps['objectPosition'];
  marketplaceOfferId?: string;
  imageSource?: string;
  segment?: SegmentTrackingProps;
  minHeight?: number;
  minWidth?: number;
  maxWidth?: number;
  maxHeight?: number;
  enableFetchPriority?: boolean;
  captureCustomQueryParams?: boolean;
}

export const DynamicImageComponent = ({
  imageUrl,
  imageLinkUrl,
  rolloverImageUrl,
  width = 200,
  height = 200,
  objectFit = 'cover',
  circularImage = false,
  marketplaceOfferId,
  imageSource,
  altText = 'image',
  objectPosition = undefined,
  segment,
  minHeight,
  minWidth,
  maxWidth,
  maxHeight,
  enableFetchPriority = false,
  captureCustomQueryParams = false,
}: DynamicImageComponentProps) => {
  const { marketplaceOffers } = useDynamicGrid();
  const searchParams = useSearchParams();
  const { impressionViewProps, handleTrackInteraction } = useDynamicAnalytics({
    enableInteractionTracking: segment?.enableInteractionTracking || false,
    entityName: segment?.entityName || '',
    impressionSection: segment?.impressionSection || '',
    impressionPosition: segment?.impressionPosition || '',
    extraProps: {
      sub_location: 'image',
    },
  });

  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  const imageObjectFit = objectFit as
    | 'cover'
    | 'contain'
    | 'fill'
    | 'none'
    | 'scale-down';

  const selectedMarketplaceOffer = useMemo(() => {
    return marketplaceOffers?.find(
      (offer: any) => offer?.id === marketplaceOfferId
    );
  }, [marketplaceOffers, marketplaceOfferId]);

  const src =
    imageSource === IMAGE_SOURCE_TYPES.MARKETPLACE_OFFER
      ? selectedMarketplaceOffer?.image?.source
      : imageUrl;

  const alt =
    imageSource === IMAGE_SOURCE_TYPES.MARKETPLACE_OFFER
      ? selectedMarketplaceOffer?.image?.alt
      : altText;

  const fetchPriority = enableFetchPriority === true ? 'high' : 'low';
  const loading = enableFetchPriority === true ? 'eager' : 'lazy';

  let imageLink = imageLinkUrl;

  if (captureCustomQueryParams) {
    const imageLinkWithCustomQueryParams = buttonUrlWithCustomQueryParams(
      imageLinkUrl || '',
      searchParams
    ).toString();
    imageLink = imageLinkWithCustomQueryParams;
  }

  if (src) {
    return (
      <SegmentImpression
        eventName="Image Viewed"
        eventProps={impressionViewProps}
      >
        <Box
          onClick={handleTrackInteraction}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={circularImage ? styles.circularImage : ''}
          style={{
            ...(width && { width: `${width}px` }),
            ...(height && { height: `${height}px` }),
            ...(minHeight && { minHeight: `${minHeight}px` }),
            ...(minWidth && { minWidth: `${minWidth}px` }),
            ...(maxWidth && { maxWidth: `${maxWidth}px` }),
            ...(maxHeight && { maxHeight: `${maxHeight}px` }),
          }}
        >
          <ImageWrapper imageLinkUrl={imageLink}>
            <Image
              objectPosition={objectPosition}
              src={isHovered && rolloverImageUrl ? rolloverImageUrl : src}
              alt={alt}
              objectFit={imageObjectFit}
              backgroundColor="transparent"
              fetchPriority={fetchPriority}
              loading={loading}
            />
          </ImageWrapper>
        </Box>
      </SegmentImpression>
    );
  }

  return <ImageIcon size={64} />;
};

export const DynamicImageForm = () => {
  const {
    tabIndex,
    imageSource,
    circularImage,
    customUrl,
    objectFit,
    height,
    width,
    marketplaceOfferId,
    imageSourceOptions,
    marketplaceOfferOptions,
    handleSetCircularImage,
    handleSetCustomUrl,
    handleSetHeight,
    handleSetWidth,
    handleSetObjectFit,
    handleSetImageSource,
    handleSetMarketplaceOffer,
    handleSelectImageSourceCallback,
    setTabIndex,
    initialCustomUrl,
    altText,
    handleSetAltText,
    handleSetObjectPosition,
    objectPosition,
    minHeight,
    minWidth,
    handleSetMinHeight,
    handleSetMinWidth,
    maxWidth,
    maxHeight,
    handleSetMaxWidth,
    handleSetMaxHeight,
    imageLinkUrl,
    rolloverImageUrl,
    handleSetImageLinkUrl,
    handleSetRolloverImageUrl,
    enableFetchPriority,
    handleSetEnableFetchPriority,
    captureCustomQueryParams,
    handleCaptureCustomQueryParamsChange,
  } = useDynamicImageForm();

  return (
    <Tabs selectedIndex={tabIndex} onSelect={(index) => setTabIndex(index)}>
      <TabList>
        <Tab>Image Options</Tab>
        <Tab>WP Media</Tab>
      </TabList>
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        paddingTop={2}
        paddingBottom={2}
      >
        <TabPanel>
          <Box display="flex" gap={2} flexDirection="column">
            <Box paddingTop={2}>
              <Select
                fullWidth
                value={imageSource}
                onChange={handleSetImageSource}
                label="Image Source"
                options={imageSourceOptions()}
              />
            </Box>
            <Box paddingTop={2}>
              {imageSource === IMAGE_SOURCE_TYPES.MEDIA_LIBRARY && (
                <Input
                  fullWidth
                  value={
                    initialCustomUrl || 'Select an Image at the WP Media tab'
                  }
                  disabled
                />
              )}
              {imageSource === IMAGE_SOURCE_TYPES.CUSTOM_URL && (
                <Input
                  fullWidth
                  label="Custom URL"
                  value={customUrl}
                  onChange={handleSetCustomUrl}
                />
              )}
              {imageSource === IMAGE_SOURCE_TYPES.MARKETPLACE_OFFER && (
                <Fragment>
                  <Select
                    fullWidth
                    value={marketplaceOfferId}
                    onChange={handleSetMarketplaceOffer}
                    label="Marketplace Offer"
                    options={marketplaceOfferOptions()}
                  />
                  <Input fullWidth value={customUrl} disabled />
                </Fragment>
              )}
            </Box>
            <Box paddingTop={2}>
              <Input
                value={altText}
                onChange={handleSetAltText}
                label="Alt Text"
                fullWidth
              />
            </Box>
            <Box paddingTop={2}>
              <Input
                value={imageLinkUrl}
                onChange={handleSetImageLinkUrl}
                label="Image Link URL"
                fullWidth
              />
            </Box>
            <Box paddingTop={2}>
              <Input
                value={rolloverImageUrl}
                onChange={handleSetRolloverImageUrl}
                label="Rollover Image URL"
                fullWidth
              />
            </Box>
            <Box paddingTop={2}>
              <CheckboxOption
                label="Enable Fetch Priority?"
                checked={enableFetchPriority}
                onChange={handleSetEnableFetchPriority}
              />
            </Box>
            <Box paddingTop={2}>
              <Input
                value={width}
                onChange={handleSetWidth}
                label="Width"
                fullWidth
              />
            </Box>
            <Box paddingTop={2}>
              <Input
                value={height}
                onChange={handleSetHeight}
                label="Height"
                fullWidth
              />
            </Box>
            <Box paddingTop={2}>
              <Input
                value={minWidth}
                onChange={handleSetMinWidth}
                label="Min Width"
                fullWidth
              />
            </Box>
            <Box paddingTop={2}>
              <Input
                value={minHeight}
                onChange={handleSetMinHeight}
                label="Min Height"
                fullWidth
              />
            </Box>
            <Box paddingTop={2}>
              <Input
                value={maxWidth}
                onChange={handleSetMaxWidth}
                label="Max Width"
                fullWidth
              />
            </Box>
            <Box paddingTop={2}>
              <Input
                value={maxHeight}
                onChange={handleSetMaxHeight}
                label="Max Height"
                fullWidth
              />
            </Box>
            <Box paddingTop={2}>
              <Select
                fullWidth
                value={objectFit}
                onChange={handleSetObjectFit}
                label="Object Fit"
                options={imageObjectFitOptions}
              />
            </Box>
            <Box paddingTop={2}>
              <Select
                fullWidth
                value={objectPosition}
                onChange={handleSetObjectPosition}
                label="Object Position"
                options={imageObjectPositionOptions}
              />
            </Box>
            <Box paddingY={2}>
              <CheckboxOption
                label="Capture Custom Query Params?"
                checked={captureCustomQueryParams}
                onChange={handleCaptureCustomQueryParamsChange}
              />
            </Box>
            <Box paddingTop={2}>
              <CheckboxOption
                label="Circular Image?"
                checked={circularImage}
                onChange={handleSetCircularImage}
              />
              <SegmentTrackingForm />
            </Box>
          </Box>
        </TabPanel>
        <TabPanel>
          <div style={{ isolation: 'isolate' }}>
            <DynamicImageWpMedia
              handleSelectImageSourceCallback={handleSelectImageSourceCallback}
            />
          </div>
        </TabPanel>
      </Box>
    </Tabs>
  );
};
